import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Icon,
  LoadingIcon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  tv,
  useToast,
} from '@eluve/components';
import { PatientOverview } from '@eluve/feature-patients';
import {
  useAppointmentContext,
  useAppointmentPatient,
  useAppointmentStatus,
  useLatestAppointmentLlmOutput,
  useListenForAppointmentLlmJobStatus,
} from '@eluve/frontend-appointment-hooks';
import { useListenForAppointmentLlmJobs } from '@eluve/frontend-appointment-hooks';
import {
  AppointmentStatusTypesLookup,
  FeedbackTypeLookup,
  LlmOutputTypesLookup,
  WorkerStatusTypesLookup,
} from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { RedFlagsOutputSchema } from '@eluve/llm-outputs';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import { DifferentialDiagnosesTabContent } from './DifferentialDiagnosesTabContent';
import { RedFlagsOutput } from './RedFlagsOutput';

// Constants

const insertFeedbackMutation = graphql(`
  mutation insertSummaryFeedback(
    $appointmentId: uuid!
    $llmOutputId: uuid
    $type: FeedbackTypeEnum!
    $summarySection: String
  ) {
    insertSummaryFeedbackOne(
      object: {
        appointmentId: $appointmentId
        llmOutputId: $llmOutputId
        type: $type
        summarySection: $summarySection
      }
    ) {
      __typename
      id
      type
    }
  }
`);

// Private Components

type InsightTab = 'REASON_FOR_VISIT' | 'DIFFERENTIAL_DIAGNOSES' | 'RED_FLAGS';

// Message keys for each tab's display label; translated at render time.
const InsightTabLabelKeys = {
  REASON_FOR_VISIT: 'appointmentPage.insights.reasonForVisitTab',
  DIFFERENTIAL_DIAGNOSES: 'appointment.insights.differentialDiagnoses',
  RED_FLAGS: 'appointmentPage.insights.considerationsTab',
} as const;

const insightTabButtonStyles = tv({
  base: '-my-px border-b-2 border-transparent px-2 py-3 text-2xs font-semibold text-text-supporting uppercase hover:text-text-tertiary',
  variants: {
    active: {
      true: 'border-text-tertiary text-text-tertiary',
    },
  },
});

type InsightTabButtonProps = {
  isActive?: boolean;
  onClick: (tab: InsightTab) => void;
  tab: InsightTab;
};

const InsightTabButton: React.FC<InsightTabButtonProps> = ({
  isActive = false,
  onClick,
  tab,
}) => {
  const { t } = useTranslation();
  const styles = insightTabButtonStyles({ active: isActive });
  return (
    <button className={styles} onClick={() => onClick(tab)}>
      {t(InsightTabLabelKeys[tab])}
    </button>
  );
};

const insightTabContentStyles = tv({
  base: '',
  variants: {
    hidden: {
      true: 'hidden',
    },
  },
});

type InsightTabContentProps = React.PropsWithChildren<{
  className?: string;
  id?: string;
  isActive?: boolean;
}>;

const InsightTabContent: React.FC<InsightTabContentProps> = ({
  children,
  className,
  id,
  isActive = false,
}) => {
  const styles = insightTabContentStyles({ className, hidden: !isActive });
  return (
    <div id={id} className={styles}>
      {children}
    </div>
  );
};

export const AppointmentInsights: React.FC = () => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState<InsightTab>('REASON_FOR_VISIT');
  const apiClient = useApiClient();
  const { appointmentId, tenantId } = useAppointmentContext();
  const appointmentStatus = useAppointmentStatus();
  const patient = useAppointmentPatient();
  const patientOverviews = patient?.patient_overviews ?? [];
  const toast = useToast();

  const isAppointmentActive =
    appointmentStatus === AppointmentStatusTypesLookup.ACTIVE ||
    appointmentStatus === AppointmentStatusTypesLookup.NOT_STARTED;

  const [insertFeedback] = useMutation(insertFeedbackMutation);
  const isPatientOverviewEnabled = useIsFeatureFlagEnabled('PATIENT_OVERVIEW');

  const { appointmentLlmOutput: redFlagsOutput, refetch } =
    useLatestAppointmentLlmOutput({
      outputType: LlmOutputTypesLookup.DIFFERENTIAL_DIAGNOSES_PLUS_RED_FLAGS,
    });

  const { status } = useListenForAppointmentLlmJobStatus({
    outputType: LlmOutputTypesLookup.DIFFERENTIAL_DIAGNOSES_PLUS_RED_FLAGS,
    shouldSubscribe: isAppointmentActive,
  });

  const { status: patientOverviewStatus } = useListenForAppointmentLlmJobStatus(
    {
      outputType: LlmOutputTypesLookup.PATIENT_OVERVIEW,
      shouldSubscribe: isPatientOverviewEnabled && isAppointmentActive,
    },
  );

  useListenForAppointmentLlmJobs({
    outputType: LlmOutputTypesLookup.DIFFERENTIAL_DIAGNOSES_PLUS_RED_FLAGS,
    lastJobCompletedAt: redFlagsOutput?.completedAt ?? null,
    onDataReceived: refetch,
  });

  const insights = redFlagsOutput?.content
    ? (redFlagsOutput.content as RedFlagsOutputSchema)
    : null;

  const hasContent = insights !== null || patientOverviews.length > 0;
  const hasDiagnoses =
    insights !== null && insights.differential_diagnoses.length > 0;

  const isGenerating = [patientOverviewStatus, status].includes(
    WorkerStatusTypesLookup.IN_PROGRESS,
  );

  const handleClick = async () => {
    try {
      const result = await apiClient.llm.generateInsights({
        params: {
          tenantId: tenantId,
          appointmentId: appointmentId,
        },
        body: { enableTools: true },
      });

      if (result.status !== 201) {
        toast.error(t('appointmentPage.insights.generateFailed'));
        return;
      }
    } catch (e) {
      toast.error(t('appointmentPage.insights.generateFailed'));
    }
  };

  const handleFeedback = async (isHelpful: boolean, content: string) => {
    await insertFeedback({
      variables: {
        appointmentId,
        summarySection: content,
        type: isHelpful
          ? FeedbackTypeLookup.POSITIVE
          : FeedbackTypeLookup.NEGATIVE,
        llmOutputId: redFlagsOutput?.llmOutputId,
      },
    });
  };

  if (!hasContent) {
    return (
      <Stack
        id="insights-container"
        className={cn(
          'mt-2 mb-4 rounded-lg bg-bg-tertiary p-3 pr-4',
          hasContent && 'h-[400px] max-h-[400px]',
        )}
        gap={3}
        direction="horizontal"
      >
        <div className="rounded-md bg-bg-insights-strong-primary p-2.5 text-text-insights-strong">
          <Icon name="Lightbulb" size="lg" />
        </div>
        <Stack>
          <Text inline weight="bold" color="secondary" size="md">
            {t('appointmentPage.insights.eluveInsights')}
          </Text>
          <Text inline color="tertiary" size="xs">
            {t('appointmentPage.insights.tagline')}
          </Text>
        </Stack>
        {isGenerating && (
          <LoadingIcon size="xl" className="mr-2 text-text-secondary" />
        )}
        {!isGenerating && (
          <Button
            variant="outline"
            size="sm"
            disabled={!isAppointmentActive}
            onClick={handleClick}
          >
            {t('appointmentPage.insights.run')}
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      align="stretch"
      id="insights-container"
      className="mt-2 mb-4 h-[400px] max-h-[400px] rounded-lg border border-border-secondary bg-bg-primary"
    >
      <Stack
        className="h-[52px] shrink-0 rounded-t-[7px] bg-bg-insights-subtle pr-4 pl-3"
        gap={3}
        direction="horizontal"
      >
        <div className="rounded-md bg-bg-insights-strong-primary p-1.5 text-text-insights-strong">
          <Icon name="Lightbulb" />
        </div>
        <Stack gap={2}>
          <Text
            inline
            weight="bold"
            size="md"
            className="text-text-insights-subtle"
          >
            {t('appointmentPage.insights.eluveInsights')}
          </Text>
        </Stack>
        {isGenerating && (
          <LoadingIcon size="xl" className="mr-1 text-text-insights-subtle" />
        )}
        {!isGenerating && hasContent && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={!isAppointmentActive}
                onClick={handleClick}
              >
                <Icon name="RefreshCw" size="auto" />
              </Button>
            </TooltipTrigger>
            {isAppointmentActive && (
              <TooltipContent>
                {t('appointmentPage.insights.rerun')}
              </TooltipContent>
            )}
          </Tooltip>
        )}
      </Stack>
      {hasContent && (
        <>
          <Stack
            className="border-b border-border-secondary px-4 pt-2"
            gap={4}
            direction="horizontal"
          >
            {Object.keys(InsightTabLabelKeys).map((tab) => {
              const insightsTab = tab as InsightTab;
              if (!isPatientOverviewEnabled && tab === 'PATIENT_OVERVIEW') {
                return null;
              }
              return (
                <InsightTabButton
                  key={insightsTab}
                  isActive={currentTab === insightsTab}
                  onClick={setCurrentTab}
                  tab={insightsTab}
                />
              );
            })}
          </Stack>
          <div className="flex-1 overflow-y-auto">
            <InsightTabContent
              className="p-6"
              isActive={currentTab === 'DIFFERENTIAL_DIAGNOSES'}
              id="differential-diagnosis-output"
            >
              <DifferentialDiagnosesTabContent />
            </InsightTabContent>
            <InsightTabContent
              className="py-4"
              isActive={currentTab === 'RED_FLAGS'}
              id="considerations-output"
            >
              {hasDiagnoses && (
                <RedFlagsOutput
                  content={insights.red_flags}
                  onFeedback={handleFeedback}
                  onShowDiagnoses={() =>
                    setCurrentTab('DIFFERENTIAL_DIAGNOSES')
                  }
                />
              )}
            </InsightTabContent>
            <InsightTabContent
              className="py-4"
              isActive={currentTab === 'REASON_FOR_VISIT'}
              id="read"
            >
              {isPatientOverviewEnabled && (
                <div className="px-4">
                  <PatientOverview />
                </div>
              )}
            </InsightTabContent>
          </div>
        </>
      )}
      {!hasContent && (
        <Stack className="p-6" gap={2}>
          <Text weight="semibold">
            {t('appointmentPage.insights.noInsightsTitle')}
          </Text>
          <Text weight="regular" color="secondary" className="mb-3">
            {t('appointmentPage.insights.noInsightsDescription')}
          </Text>
          <Button
            variant="outline"
            size="sm"
            disabled={!isAppointmentActive || isGenerating}
            onClick={handleClick}
          >
            {t('appointmentPage.insights.rerun')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
